import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { VENDOR_TYPES } from "@/lib/options";
import { Save, Sparkles, Plus, ExternalLink, Users, FileText, Clock, CheckCircle2 } from "lucide-react";

export default function VendorDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [opps, setOpps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/vendors/me"); setProfile(data || null); } catch {}
      try { const { data } = await api.get("/opportunities", { params: { mine: true, active_only: false }}); setOpps(data); } catch {}
    })();
  }, []);

  const approved = user?.vendor_status === "approved";
  if (!profile) return <Layout><div className="max-w-3xl mx-auto px-6 py-24 text-muted">Loading…</div></Layout>;

  const set = (k, v) => setProfile({ ...profile, [k]: v });
  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.put("/vendors/me", {
        business_name: profile.business_name, vendor_type: profile.vendor_type,
        portfolio_url: profile.portfolio_url || "",
        location_city: profile.location_city, location_state: profile.location_state,
        location_country: profile.location_country, website: profile.website,
        instagram: profile.instagram, bio: profile.bio,
      });
      setProfile(data); setMsg("Saved!"); setTimeout(()=>setMsg(""), 1800);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error saving");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <p className="qld-overline mb-2">vendor dashboard</p>
            <h1 className="font-serif text-4xl md:text-5xl text-ink">{profile.business_name}</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {approved ? (
              <span className="qld-chip qld-chip-sage inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5}/> Approved
              </span>
            ) : (
              <span className="qld-chip qld-chip-butter inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.5}/> Pending review (within 24h)
              </span>
            )}
            {approved && (
              <>
                <Link to="/directory" className="qld-btn-primary" data-testid="vendor-browse-cta">
                  <Users className="w-4 h-4" strokeWidth={1.5}/> Browse the directory
                </Link>
                <Link to="/dashboard/vendor/post" className="qld-btn-coral" data-testid="vendor-post-cta">
                  <Plus className="w-4 h-4" strokeWidth={1.5}/> Post an opportunity
                </Link>
              </>
            )}
          </div>
        </div>

        {!approved && (
          <div className="mb-10 p-6 rounded-3xl bg-lilac border-2 border-black">
            <div className="flex gap-3">
              <Sparkles className="w-5 h-5 text-ink mt-0.5 shrink-0" strokeWidth={1.5}/>
              <div>
                <h3 className="font-serif text-xl text-ink mb-1">You're on the list to be reviewed.</h3>
                <p className="text-black/80 text-sm">
                  We manually approve each vendor to protect the people in this platform. Hang tight - you'll be approved within 24 hours.
                  Use the time to fill out your profile below.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-10">
          <section className="lg:col-span-2 space-y-5">
            <h2 className="font-serif text-2xl text-ink">Your vendor profile</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Business / Brand name">
                <input className="qld-input" value={profile.business_name||""} onChange={(e)=>set("business_name", e.target.value)} data-testid="vendor-name"/>
              </Field>
              <Field label="Vendor type">
                <select className="qld-input" value={profile.vendor_type||""} onChange={(e)=>set("vendor_type", e.target.value)} data-testid="vendor-type">
                  <option value="">Choose…</option>
                  {VENDOR_TYPES.map((v) => <option key={v}>{v}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Portfolio link">
              <input className="qld-input" placeholder="https://" value={profile.portfolio_url||""} onChange={(e)=>set("portfolio_url", e.target.value)} data-testid="vendor-portfolio"/>
            </Field>
            <div className="grid md:grid-cols-3 gap-5">
              <Field label="City"><input className="qld-input" value={profile.location_city||""} onChange={(e)=>set("location_city", e.target.value)} data-testid="vendor-city"/></Field>
              <Field label="State / Region"><input className="qld-input" value={profile.location_state||""} onChange={(e)=>set("location_state", e.target.value)} data-testid="vendor-state"/></Field>
              <Field label="Country"><input className="qld-input" value={profile.location_country||""} onChange={(e)=>set("location_country", e.target.value)} data-testid="vendor-country"/></Field>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Website"><input className="qld-input" placeholder="https://" value={profile.website||""} onChange={(e)=>set("website", e.target.value)} data-testid="vendor-website"/></Field>
              <Field label="Instagram"><input className="qld-input" placeholder="@yourhandle" value={profile.instagram||""} onChange={(e)=>set("instagram", e.target.value)} data-testid="vendor-instagram"/></Field>
            </div>
            <Field label="About your work">
              <textarea rows={5} className="qld-input" value={profile.bio||""} onChange={(e)=>set("bio", e.target.value)} data-testid="vendor-bio"/>
            </Field>
            <div className="flex items-center gap-4">
              <button onClick={save} disabled={busy} className="qld-btn-primary" data-testid="vendor-save-btn">
                <Save className="w-4 h-4" strokeWidth={1.5}/> {busy ? "Saving…" : "Save"}
              </button>
              {msg && <span className="text-sm text-muted">{msg}</span>}
            </div>
          </section>

          <section className="lg:col-span-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl text-ink">My opportunities</h2>
              {approved && (
                <Link to="/dashboard/vendor/post" className="text-sm font-medium underline decoration-2 underline-offset-4 decoration-wine hover:text-wine transition" data-testid="vendor-post-small">
                  + New
                </Link>
              )}
            </div>
            {opps.length === 0 ? (
              <div className="qld-card p-6 text-center">
                <FileText className="w-6 h-6 text-wine mx-auto mb-2" strokeWidth={1.5}/>
                <p className="text-muted text-sm">No opportunities posted yet.</p>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="vendor-my-opps">
                {opps.map((o) => (
                  <li key={o.opp_id} className="qld-card p-4">
                    <p className="font-serif text-lg text-ink">{o.title}</p>
                    <p className="text-xs text-muted mb-2">{o.type} · {o.is_active ? "active" : "closed"}</p>
                    <Link to={`/dashboard/vendor/opportunity/${o.opp_id}`} className="text-wine text-sm hover:underline inline-flex items-center gap-1 font-medium" data-testid={`vendor-view-opp-${o.opp_id}`}>
                      View applicants <ExternalLink className="w-3 h-3" strokeWidth={1.5}/>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink mb-1.5">{label}</span>
      {children}
    </label>
  );
}
